/**
 * Entity Data Processing — Helpers and From-API Deserialization
 *
 * Pure helpers used by the API submission processor and the deserializer
 * that turns API responses back into in-memory objects.
 */

import type {
  BaseEntity,
  SimpleFieldConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import { HUB_FOREIGN_KEY_MAP } from "@/lib/schema/foreign-key-map";
import { logger } from "../../../logger";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Filter out relation data that shouldn't be sent to the API
 * Relation fields are populated by Supabase JOINs but aren't actual database columns
 * Also filters out FTS-specific fields (relevance, total_count) that are added by search results
 */
export const filterRelationData = <T extends BaseEntity>(item: T): T => {
  const relationFields = [
    "contacts",
    "users",
    "assigned_user",
    "contact",
    "tickets",
    "companies",
    "app_users",
    "communication_types",
    "roles",
  ];
  const metadataRelationFields = Array.from(
    new Set(Object.values(HUB_FOREIGN_KEY_MAP).map((config) => config.referencedTable))
  );

  // FTS-specific fields that should be removed from inline edit payloads
  // These are added by useFtsSearchWithFullRows for UI state (loading, relevance, partial data)
  const ftsFields = [
    "relevance",
    "total_count",
    "_relevance",
    "_isPartial",
    "_isLoading",
  ];

  const fieldsToRemove = [...relationFields, ...metadataRelationFields, ...ftsFields];
  const filteredItem = { ...item };

  fieldsToRemove.forEach((field) => {
    if (field in filteredItem) {
      delete filteredItem[field as keyof T];
    }
  });

  // Also remove any fields that are GraphQL objects (have __typename property)
  // This catches any nested objects from GraphQL responses
  Object.keys(filteredItem).forEach((key) => {
    const value = filteredItem[key as keyof T];
    if (value && typeof value === "object" && "__typename" in value) {
      delete filteredItem[key as keyof T];
    }
  });

  return filteredItem;
};

/**
 * Resolve user IDs, replacing "me" with actual user ID
 * This handles the case where users select "Assign to Me" in the UI
 */
export const resolveUserIds = (userIds: string[], currentUserId?: string): string[] => {
  return userIds.map((userId) => {
    if (userId === "me" && currentUserId) {
      return currentUserId;
    }
    return userId;
  });
};

/**
 * Process tags field - convert comma-separated string to array
 * This handles the case where tags are entered as comma-separated values
 */
export const processTagsField = (value: unknown): string[] => {
  if (typeof value === "string") {
    return value
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map((tag) => String(tag));
  }
  return [];
};

// ============================================================================
// FROM-API DESERIALIZATION
// ============================================================================

/**
 * Process entity data received from API
 * This deserializes JSON fields that were stored as strings
 */
export const processEntityDataFromApi = <T extends BaseEntity>(
  item: T,
  fieldsConfig: Record<string, SimpleFieldConfig>
): T => {
  // Mutable string-keyed view: JSON fields are rewritten from stored strings
  // back to parsed objects, which does not fit the original `T[keyof T]` types.
  const processedItem = { ...item } as Record<string, unknown>;

  // Process each field based on its type
  Object.keys(fieldsConfig).forEach((fieldId) => {
    const fieldConfig = fieldsConfig[fieldId];
    const fieldValue = processedItem[fieldId];

    switch (fieldConfig.type) {
      case "json":
        // Deserialize JSON strings back to objects OR arrays. pg_graphql hands
        // jsonb columns to Apollo as JSON strings (see
        // SUPABASE_GRAPHQL_JSONB_READ_IS_STRING). A jsonb ARRAY field
        // (e.g. companies.technical_identifiers) parses to an array — it MUST
        // be kept, not dropped: leaving the raw string makes downstream
        // Array.isArray/.map silently no-op. Arrays are valid JSON objects.
        if (typeof fieldValue === "string") {
          try {
            const parsed: unknown = JSON.parse(fieldValue);
            if (typeof parsed === "object" && parsed !== null) {
              processedItem[fieldId] = parsed;
            }
          } catch {
            logger.warn(`Failed to deserialize JSON field ${fieldId}`, { fieldId, fieldValue });
            // Keep the string as-is if parsing fails
          }
        }
        // null/undefined and already-object values are left as-is
        break;

      // Add more field types as needed
      default:
        // No processing needed for other field types
        break;
    }
  });

  return processedItem as T;
};
